import React, { useState, useEffect, ChangeEvent } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Typography,
  Tooltip,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  IconButton,
} from "@mui/material";
import { getJwtTokenFromIdToken } from "../auth/AuthFanc";
import BackButton from "../../atoms/Button/BrowserBackButton";
import PageTitle from "../../molecules/PageTitle";
import {
  CustomContainer,
  customDataGridLocaleTextNoData,
} from "../../../styles/styles";
import { useParams } from "react-router-dom";
import {
  DataGrid,
  GridColDef,
  GridValueGetterParams,
  jaJP,
} from "@mui/x-data-grid";
import { isUndefined } from "util";

interface SCILog {
  ts: number;
  originalTs: number;
  v: string;
  k: number;
  os: number;
  cs: number;
  ot: number;
  ow: number;
  oa: number;
  an: number;
  bp: number;
  sg: number;
  hc: number;
}

interface DoorLog {
  ts: number; // 日時
  state: string; // 状態
  openSpeed2: boolean; // 開扉速度2
  closeSpeed2: boolean; // 閉扉速度2
  openHalf: boolean; // 半開
  openHoldSecond: number; // 開扉時間
  txPower: number; //
  lockCommand: boolean; //
  lockSense: boolean; //
}

const DoorLogPage: React.FC = () => {
  const { autoSdcId } = useParams();
  // ラジオボタンで選択しているもの
  const [select, setSelect] = useState("1");
  // 表示しているデータ
  const [logType, setLogType] = useState("1");
  const [doorLogs, setDoorLogs] = useState<DoorLog[]>([]);
  const [sciLogs, setSciLogs] = useState<SCILog[]>([]);
  const [building, setBuilding] = useState<
    | {
        autoBuildingId: number;
        buildingName: string;
      }
    | undefined
  >(undefined);
  const [sdc, setSdc] = useState<
    | {
        autoSdcId: number;
        sdcSerialNumber: string;
        doorPlace: string;
      }
    | undefined
  >(undefined);
  const [fromDate, setFromDate] = useState("");
  const [untilDate, setUntilDate] = useState("");
  const [logLimit, setLogLimit] = useState<number | null>(1000); //ログの取得件数
  const [currentDate, setCurrentDate] = useState<Date>(new Date()); //取得日時

  const [logsFetched, setLogsFetched] = useState(false);

  const handleLogLimit = (event: any) => {
    const value = event.target.value;
    if (!value) {
      // if value is null
      setLogLimit(value);
    } else {
      const int = parseInt(value);
      if (int < 0) {
        setLogLimit(0);
      } else {
        setLogLimit(parseInt(value));
      }
    }
  };

  const handleRadioButtonChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newLogType = event.target.value;
    setSelect(newLogType);
    setLogsFetched(false); // ラジオボタンが変更されたらリセット
  };

  const fetchLogs = async () => {
    const selectedLogType = select === "1" ? "sci" : "door";

    let queryParams = `?limit=${logLimit}`;

    // fromDate と untilDate が設定されていれば、UNIX タイムスタンプに変換してクエリパラメータに追加
    if (fromDate) {
      const fromTimestamp = new Date(fromDate).getTime();
      queryParams += `&from=${fromTimestamp}`;
    }
    if (untilDate) {
      // untilDate での日付の終わり（23:59:59）を取得するために、1日分のミリ秒を追加してから1ミリ秒引く
      const untilTimestamp =
        new Date(untilDate).getTime() + 24 * 60 * 60 * 1000 - 1;
      queryParams += `&until=${untilTimestamp}`;
    }

    const url = `${process.env.REACT_APP_API_ENDPOINT}/sdcs/${autoSdcId}/${
      selectedLogType === "sci" ? "sci-logs" : "door-logs"
    }${queryParams}`;
    const jwtToken = await getJwtTokenFromIdToken();
    // call listSciLogs | listDoorLogs API
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: jwtToken,
      },
    });

    if (!response.ok) throw new Error("Network response was not ok");

    const data = await response.json();
    if (selectedLogType === "sci") {
      setSciLogs(data.sciLogs);
    } else {
      setDoorLogs(data.doorLogs);
    }
    setBuilding(data.building);
    setSdc(data.sdc);
    setCurrentDate(new Date());
    setLogsFetched(true); // データが正常にフェッチされたら true に設定
    setLogType(selectedLogType); // データ取得後にlogTypeを更新します。
  };

  useEffect(() => {
    // コンポーネントがマウントされた時にログを自動的にフェッチ
    fetchLogs();
  }, []); // 空の依存配列を渡して、コンポーネントがマウントされた時にのみ実行されるようにします

  // ページタイトルを決定する関数
  const getPageTitle = () => {
    if (!logsFetched) {
      return "ログ"; // 初期表示タイトル
    }
    return logType === "sci" ? "ミライロドアログ" : "SDC（ドア）ログ"; // ログがフェッチされた後のタイトル
  };

  const translateUserType = (value: any): string => {
    const types: { [key: number]: string } = {
      0: "支援なし（または無変更）",
      1: "荷物多い",
      2: "お年寄り",
      3: "妊婦",
      4: "ベビーカー",
      5: "オストメイト",
      6: "聴覚障害",
      7: "視覚障害",
      8: "杖、松葉杖",
      9: "車いす",
      10: "電動車いす",
    };
    return types[value] ?? "未定義";
  };

  const translateOpenSpeedTypes = (value: number): string => {
    const types: { [key: number]: string } = {
      0: "無変更",
      1: "通常速度",
      2: "高速",
    };
    return types[value] ?? "未定義";
  };

  const translateCloseSpeedTypes = (value: number): string => {
    const types: { [key: number]: string } = {
      0: "無変更",
      1: "通常速度",
      2: "低速",
    };
    return types[value] ?? "未定義";
  };

  const translateWidthTypes = (value: number): string => {
    const types: { [key: number]: string } = {
      0: "無変更",
      1: "全開",
      2: "半開",
    };
    return types[value] ?? "未定義";
  };

  const translateBooleanOption = (value: number): string => {
    return value === 0 ? "OFF" : "ON";
  };

  // true/falseの値に基づいて表示するテキストを決定する関数
  const translateBoolean = (state: boolean) => {
    if (state) return "ON";
    return "OFF";
  };

  const downloadCSV = () => {
    const currentDateRow = [`履歴取得日時: ${currentDate.toLocaleString()}`]; // 現在の日時を行に追加
    const buildingIdRow = building
      ? [`物件システムID: ${building.buildingName}`]
      : [];
    const sdcSerialNumberRow = sdc ? [`SDC SN: ${sdc.sdcSerialNumber}`] : [];
    if (logType == "sci") {
      const header = [
        "操作日時",
        "困難種別",
        // "第2開速度の設定",
        // "第2閉速度の設定",
        // "開放延長時間（秒）",
        // "開放幅",
        // "接近距離で開ける",
        // "開閉時にドアから音声",
        // "鈴鳴距離でドア誘導音",
        // "サイネージに案内表示",
        // "介助要請",
      ];
      const csvRows = sciLogs.map((log) => [
        new Date(log.ts).toLocaleString(),
        translateUserType(log.k),
        // translateOpenSpeedTypes(log.os),
        // translateCloseSpeedTypes(log.cs),
        // log.ot,
        // translateWidthTypes(log.ow),
        // translateBooleanOption(log.oa),
        // translateBooleanOption(log.an),
        // translateBooleanOption(log.bp),
        // translateBooleanOption(log.sg),
        // translateBooleanOption(log.hc),
      ]);

      const csvContent = [
        ...currentDateRow,
        ...buildingIdRow,
        ...sdcSerialNumberRow,
        header,
        ...csvRows.map((row: any) => row.join(",")),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "social-logs.csv";
      link.click();
    } else {
      const header = [
        "操作日時",
        "動作",
        "開速度2",
        "閉速度2",
        "半開",
        "全開時間",
        "Txパワー",
        "lockCommand",
        "lockSense",
      ];
      const csvRows = doorLogs.map((log) => [
        new Date(log.ts).toLocaleString(),
        log.state,
        translateBoolean(log.openSpeed2),
        translateBoolean(log.closeSpeed2),
        translateBoolean(log.openHalf),
        log.openHoldSecond,
        log.txPower,
        translateBoolean(log.lockCommand),
        translateBoolean(log.lockSense),
      ]);

      const csvContent = [
        ...currentDateRow,
        ...buildingIdRow,
        ...sdcSerialNumberRow,
        header,
        ...csvRows.map((row: any) => row.join(",")),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Door-logs.csv";
      link.click();
    }
  };

  // SCIのグリッドの列の定義
  const sciColumns: GridColDef[] = [
    {
      field: "ts",
      headerName: "操作日時",
      flex: 1,
      valueGetter: (params: GridValueGetterParams<SCILog>) => {
        const timestamp = params.row.ts;
        return isNaN(timestamp) ? "" : new Date(timestamp).toLocaleString();
      },
    },
    {
      field: "k",
      headerName: "困難種別",
      flex: 1,
      valueGetter: (params) => translateUserType(params.row.k),
    },
    // {
    //   field: "os",
    //   headerName: "第2開速度の設定",
    //   flex: 1,
    //   valueGetter: (params) => translateOpenSpeedTypes(params.row.os),
    // },
    // {
    //   field: "cs",
    //   headerName: "第2閉速度の設定",
    //   flex: 1,
    //   valueGetter: (params) => translateCloseSpeedTypes(params.row.cs),
    // },
    // {
    //   field: "ot",
    //   headerName: "開放延長時間（秒）",
    //   flex: 1,
    //   valueGetter: (params) => params.row.ot,
    // },
    // {
    //   field: "ow",
    //   headerName: "開放幅",
    //   flex: 1,
    //   valueGetter: (params) => translateWidthTypes(params.row.ow),
    // },
    // {
    //   field: "oa",
    //   headerName: "接近距離で開ける",
    //   flex: 1,
    //   valueGetter: (params) => translateBooleanOption(params.row.oa),
    // },
    // {
    //   field: "an",
    //   headerName: "開閉時にドアから音声",
    //   flex: 1,
    //   valueGetter: (params) => translateBooleanOption(params.row.an),
    // },
    // {
    //   field: "bp",
    //   headerName: "鈴鳴距離でドア誘導音",
    //   flex: 1,
    //   valueGetter: (params) => translateBooleanOption(params.row.bp),
    // },
    // {
    //   field: "sg",
    //   headerName: "サイネージに案内表示",
    //   flex: 1,
    //   valueGetter: (params) => translateBooleanOption(params.row.sg),
    // },
    // {
    //   field: "hc",
    //   headerName: "介助要請",
    //   flex: 1,
    //   valueGetter: (params) => translateBooleanOption(params.row.hc),
    // },
  ];

  // SDC（ドア）のグリッドの列の定義
  const doorColumns: GridColDef[] = [
    {
      field: "ts",
      headerName: "操作日時",
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={new Date(params.value).toLocaleString()}>
          <span>{new Date(params.value).toLocaleString()}</span>
        </Tooltip>
      ),
    },
    {
      field: "state",
      headerName: "動作",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "openSpeed2",
      headerName: "開速度2",
      flex: 1,
      renderCell: (params) => {
        return (
          <Tooltip title={translateBoolean(params.value)}>
            <span>{translateBoolean(params.value)}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "closeSpeed2",
      headerName: "閉速度2",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={translateBoolean(params.value)}>
          <span>{translateBoolean(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: "openHalf",
      headerName: "半開",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={translateBoolean(params.value)}>
          <span>{translateBoolean(params.value)}</span>
        </Tooltip>
      ),
    },
    {
      field: "openHoldSecond",
      headerName: "全開時間",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "txPower",
      headerName: "Txパワー",
      flex: 1,
      renderCell: (params) => params.value,
    },
    {
      field: "lockCommand",
      headerName: "lockCommand",
      flex: 1,
      renderCell: (params) => translateBoolean(params.value),
    },
    {
      field: "lockSense",
      headerName: "lockSense",
      flex: 1,
      renderCell: (params) => translateBoolean(params.value),
    },
  ];

  // グリッドの行のデータ
  const sciRows = sciLogs.map((log, index) => ({
    id: index,
    ...log,
  }));
  // グリッドの行のデータ
  const doorRows = doorLogs.map((log, index) => ({
    id: index,
    ...log,
  }));

  return (
    <CustomContainer maxWidth={false}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        marginBottom={2}
      >
        <PageTitle title={getPageTitle()} />
        <BackButton />
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-end"
        marginBottom={2}
      >
        <Box>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell component="th" scope="row">
                  取付先名（物件名称）:
                </TableCell>
                <TableCell>{building && building.buildingName}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  SDC SN
                </TableCell>
                <TableCell> {sdc && sdc.sdcSerialNumber}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell component="th" scope="row">
                  履歴取得日時:
                </TableCell>
                <TableCell>{currentDate.toLocaleString()}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box
            display="flex"
            justifyContent="flex-start"
            alignItems="center"
            marginRight={6}
          >
            <TextField
              value={logLimit}
              label="取得件数"
              style={{ width: "100px" }}
              variant="outlined"
              color="primary"
              type="number"
              onChange={(event: any) => handleLogLimit(event)}
              size="small"
            />
            <TextField
              label="〜から"
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              label="〜まで"
              type="date"
              size="small"
              value={untilDate}
              sx={{ marginRight: 2 }}
              onChange={(e) => setUntilDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <FormControl>
              <RadioGroup row value={select} onChange={handleRadioButtonChange}>
                <FormControlLabel
                  value="1"
                  control={<Radio />}
                  label="ミライロドアログ"
                />
                <FormControlLabel
                  value="0"
                  control={<Radio />}
                  label="SDC（ドア）ログ"
                />
              </RadioGroup>
            </FormControl>
            <Button
              onClick={() => fetchLogs()}
              variant="contained"
              color="primary"
            >
              ログ取得
            </Button>
          </Box>
          <Box>
            <Button onClick={downloadCSV} variant="outlined" color="primary">
              CSVダウンロード
            </Button>
          </Box>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        {logsFetched && (
          <DataGrid
            rows={logType === "sci" ? sciRows : doorRows}
            columns={logType === "sci" ? sciColumns : doorColumns}
            localeText={customDataGridLocaleTextNoData}
            autoHeight
          />
        )}
      </TableContainer>
    </CustomContainer>
  );
};

export default DoorLogPage;
